/**
 * Shapefile sidecar grouping for multi-file drag-drop (WP-D).
 *
 * When operators drop `.shp` + `.shx` + `.dbf` (+ optional `.prj`) together,
 * the converter requires all sidecars beside the primary file in DATA_IN.
 */

export const REQUIRED_SHAPEFILE_SIDECAR_EXTENSIONS = ['.shx', '.dbf'] as const;
export const OPTIONAL_SHAPEFILE_SIDECAR_EXTENSIONS = ['.prj', '.cpg'] as const;

export interface ShapefileDropBundle {
  readonly primary: File;
  readonly sidecars: readonly File[];
}

function stem(name: string): string {
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot === -1 ? lower : lower.slice(0, dot);
}

function extension(name: string): string {
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot === -1 ? '' : lower.slice(dot);
}

export function isShapefileSidecarFile(name: string): boolean {
  const ext = extension(name);
  return (
    (REQUIRED_SHAPEFILE_SIDECAR_EXTENSIONS as readonly string[]).includes(ext) ||
    (OPTIONAL_SHAPEFILE_SIDECAR_EXTENSIONS as readonly string[]).includes(ext)
  );
}

/** Return basenames of required sidecars missing from the bundle. */
export function missingRequiredShapefileSidecars(
  primaryName: string,
  sidecars: readonly File[],
): string[] {
  const base = stem(primaryName);
  const present = new Set(sidecars.map((f) => f.name.toLowerCase()));
  const missing: string[] = [];
  for (const ext of REQUIRED_SHAPEFILE_SIDECAR_EXTENSIONS) {
    if (!present.has(`${base}${ext}`)) missing.push(`${base}${ext}`);
  }
  return missing;
}

/**
 * Partition a multi-file drop into shapefile bundles and standalone files.
 * Orphan sidecars (no matching `.shp` in the batch) are returned as standalone.
 */
export function groupDroppedFiles(files: readonly File[]): {
  readonly shapefileBundles: readonly ShapefileDropBundle[];
  readonly standaloneFiles: readonly File[];
} {
  const buckets = new Map<string, { shp?: File; sidecars: File[] }>();
  const standalone: File[] = [];

  for (const file of files) {
    const ext = extension(file.name);
    if (ext === '.shp') {
      const key = stem(file.name);
      const entry = buckets.get(key) ?? { sidecars: [] };
      entry.shp = file;
      buckets.set(key, entry);
      continue;
    }
    if (isShapefileSidecarFile(file.name)) {
      const key = stem(file.name);
      const entry = buckets.get(key) ?? { sidecars: [] };
      entry.sidecars.push(file);
      buckets.set(key, entry);
      continue;
    }
    standalone.push(file);
  }

  const shapefileBundles: ShapefileDropBundle[] = [];
  for (const entry of buckets.values()) {
    if (entry.shp) {
      shapefileBundles.push({ primary: entry.shp, sidecars: entry.sidecars });
    } else {
      standalone.push(...entry.sidecars);
    }
  }

  return { shapefileBundles, standaloneFiles: standalone };
}
